import { useState, useEffect, useCallback } from 'react';
import { Autocomplete, AutocompleteItem, Spinner } from "@heroui/react";
import { useTranslation } from 'react-i18next';
import { GooglePlace } from '@bitecritic-api/src/places/places.controller';
import { useAuthModal } from '~/hooks/useAuthModal';
import useUserContext from '~/hooks/useUserContext';
import { useLocale } from '~/hooks/useLocale';
import CountryAutocomplete from '../CountryAutocomplete/CountryAutocomplete';
import { CountriesEntity } from '@bitecritic-api/src/countries/countries.entity';
import useDebouncedCallback from '~/hooks/useDebouncedCallback';
import { useAutocomplete } from '~/hooks/usePlaces';

interface PlaceAutocompleteProps {
  onChange: (place: GooglePlace | undefined) => void;
  value: string | undefined;
  isDisabled?: boolean;
  variant?: 'search' | 'review';
  initialPlace?: GooglePlace | null;
  setInitialPlace?: (place: GooglePlace | null) => void;
  isGlobal?: boolean;
}

export default function PlaceAutocomplete({ onChange, value, isDisabled, variant = 'search', initialPlace, setInitialPlace, isGlobal = false }: PlaceAutocompleteProps) {
  const [input, setInput] = useState('');
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();
  const [isRateLimited, setIsRateLimited] = useState(false);
  const { requireAuth } = useAuthModal();
  const { user } = useUserContext();
  const { currentLocale } = useLocale();
  const [selectedCountry, setSelectedCountry] = useState<CountriesEntity | undefined>(undefined);

  // Extract language code from locale
  const languageCode = currentLocale?.code?.split('-')[0];

  // Use either the selected country code or the one from currentLocale as fallback
  const countryCode = isGlobal ? undefined : selectedCountry?.code || currentLocale?.code?.split('-')[1];

  // Use the cached autocomplete query
  const {
    data: places = [],
    isLoading,
    error, 
    refetch
  } = useAutocomplete();

  const handleCountryChange = useCallback((country: CountriesEntity | undefined) => {
    setSelectedCountry(country);
    // Re-fetch when country changes if we have input
    if (input.length > 2) {
      performSearch(input);
    }
  }, [input]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set input value when initialPlace is provided
  useEffect(() => {
    if (initialPlace?.name) {
      setInputValue(initialPlace.name);
    }
  }, [initialPlace]);

  const debouncedSetInput = useDebouncedCallback((value: string) => {
    setInput(value);
    performSearch(value);
  }, 500);

  // Function to perform the search
  const performSearch = async (searchText: string) => {
    if (searchText.length <= 2) return;
    
    try {
      await refetch(searchText, languageCode, countryCode);
      setIsRateLimited(false);
    } catch (error: any) {
      if (error?.response?.status === 429) {
        setIsRateLimited(true);
      }
      console.error('Error searching places:', error);
    }
  };

  if (error && !isRateLimited) {
    return <div className="text-danger">{t('common.error.fetchFailed')}</div>;
  }

  const getLabel = () => {
    if (variant === 'review') {
      return t('places.whereDidYouEat');
    }
    return t('places.searchByLocation');
  };

  const handleInputChange = (value: string) => {
    if (!user) {
      requireAuth(() => {
        setInputValue(value);
        debouncedSetInput(value);
      });
      return;
    }
    setInputValue(value);
    debouncedSetInput(value);
    if (!value) {
      setInitialPlace?.(null);
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    if (key === null) {
      onChange(undefined);
      return;
    }

    if (!user && variant === 'review') {
      requireAuth(() => {
        const place = places?.find((p: GooglePlace) => p.id === key);
        onChange(place);
        if (place) {
          setInputValue(place.name);
        }
      });
      return;
    }

    const place = places?.find((p: GooglePlace) => p.id === key);
    onChange(place);
    if (place) {
      setInputValue(place.name);
    }
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 gap-4">

        {!isGlobal && (
          <div className="md:col-span-12">
            <CountryAutocomplete
              onChange={handleCountryChange}
              value={selectedCountry?.id}
              label={t('common.country')}
              useCurrentLocale={true}
            />
          </div>
        )}
        <div className={`md:col-span-12`}>
          <Autocomplete
            autoFocus={false}
            label={getLabel()}
            placeholder={t('places.selectPlace') ?? ''}
            onSelectionChange={handleSelectionChange}
            selectedKey={value}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            endContent={isLoading && <Spinner size="sm" />}
            defaultItems={initialPlace ? [initialPlace] : []}
            items={places ?? []}
            isDisabled={isDisabled || isRateLimited}
            errorMessage={isRateLimited ? t('common.error.rateLimitExceeded') : undefined}
            className="w-full"
          >
            {(item: GooglePlace) => (
              <AutocompleteItem key={item.id} textValue={item.name}>
                <div className="flex justify-between items-center">
                  <div className="flex gap-2 items-center">
                    <div className="flex flex-col">
                      <span className="text-small">{item.name.split(',')[0]}</span>
                      <span className="text-tiny">{item.name.split(',').slice(1).join(',').trim()}</span>
                    </div>
                  </div>
                </div>
              </AutocompleteItem>
            )}
          </Autocomplete>
        </div>
      </div>
    </div>
  );
}